import React, {useEffect, useState} from 'react';
import { Grid, Typography, Container, Card, CardContent, CardMedia, Box, Skeleton, Pagination, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getCategories } from '../services/api';
import { Category } from '../types/Category';
import { SnackbarProvider, enqueueSnackbar } from 'notistack';
import { standardCardStyle, cardMediaStyle, cardContentStyle, cardTitleStyle } from '../styles/cardStyles';

const Categories = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const limit = 6;
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<object | any>({})
  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    const fetchCategories = async (paging = 1, limit = 6) => {
      try {
        setIsLoading(true)
        const { data }: any = await getCategories(paging, limit);
        const formattedCategories = (data.categories as any[]).map(({ _id, ...rest }) => ({
          id: _id,
          ...rest
        }));
        setPagination(data.pagination);
        setCategories(formattedCategories as any)
      } catch (error) {
        console.log("error", error);
        enqueueSnackbar((error as any).message, { variant: 'error' });
      } finally {
        setIsLoading(false);
      }
    }
    fetchCategories(page, limit)
  },[page, limit]);

  const handleCategoryClick = (categoryId: string) => {
    navigate(`/posts?categoryId=${encodeURIComponent(categoryId)}`);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };
  
  return (
    <Container maxWidth="lg">
      <Typography variant="h4" component="h1" gutterBottom sx={{ my: 4 }}>
        Blog Categories
      </Typography>
      <Grid container spacing={4}
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          '& .MuiGrid-item': {
            display: 'flex',
          },
        }}
      >
        {isLoading ? (
          <Grid container spacing={4}>
            {[...Array(6)].map((_, index) => (
              // @ts-ignore - Ignoring the item prop type issue
              <Grid item key={index} xs={12} sm={6} md={4}>
                <Stack spacing={1}>
                  <Skeleton variant="rectangular" width="300px" height={200} />
                  <Skeleton variant="text" width="80%" height={24} />
                  <Skeleton variant="text" width="60%" height={20} />
                  <Skeleton variant="text" width="40%" height={16} />
                </Stack>
              </Grid>
            ))}
          </Grid>
        ) : categories.length > 0 ? (
            <>
              {categories.map((category) => (
                // @ts-ignore - Ignoring the item prop type issue
                <Grid item key={category.id} xs={12} sm={6} md={4}
                  sx={{
                    '& > *': {
                      width: '100%',
                      height: '100%',
                    },
                  }}
                >
                  <Card 
                    sx={{ 
                      ...standardCardStyle,
                      cursor: 'pointer',
                    }}
                    onClick={() => handleCategoryClick(category.id)}
                  >
                    <CardMedia
                      component="img"
                      image={category.imageUrl}
                      alt={category.name}
                      sx={cardMediaStyle}
                    />
                    <CardContent sx={cardContentStyle}>
                      <Box>
                        <Typography variant="h6" component="h2" sx={cardTitleStyle}>
                          {category.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          {category.description}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {category.postCount} posts
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </>
          ) : (
            <Typography variant="h6" component="h2" sx={cardTitleStyle}>
              No Data
            </Typography>
          )
        }
      </Grid>
      {pagination?.total > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={pagination?.pages}
            page={pagination?.page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}
      <SnackbarProvider />
    </Container>
  );
};

export default Categories; 